
        <div class="space-y-8 pb-12">
            <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <p class="text-sm font-semibold text-[color:var(--text-subtle)]">
                        <ng-container *ngIf="user$ | async as user">
                            <span *ngIf="displayCompanyName(user) as companyName">
                                <span>{{ companyName }}</span>
                                <span class="mx-1">·</span>
                            </span>
                        </ng-container>
                        <span>{{ todayLabel }}</span>
                    </p>
                    <h1 class="text-4xl font-bold tracking-tight">Partner Dashboard</h1>
                    <p class="mt-2 text-lg text-[color:var(--muted-foreground)]">Welcome back! Here's your property portfolio overview</p>
                </div>
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
                    <label *ngIf="canExportReports()" class="inline-flex h-11 items-center gap-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--card)] px-3 text-sm font-semibold text-[color:var(--foreground)] shadow-sm">
                        <lucide-icon name="folder-kanban" class="h-4 w-4 text-[color:var(--text-subtle)]"></lucide-icon>
                        <select
                            [ngModel]="selectedProjectId"
                            (ngModelChange)="setProject($event)"
                            [disabled]="reportProjectsLoading || !!exportingFullReportFormat"
                            aria-label="Project to display on the dashboard"
                            class="max-w-[13rem] bg-transparent text-sm font-semibold focus:outline-none disabled:opacity-60"
                        >
                            <option value="">All projects</option>
                            <option *ngFor="let project of reportProjects" [value]="project.id">{{ project.name }}</option>
                        </select>
                    </label>
                    <p *ngIf="dashboardError" role="alert" class="text-sm text-red-600">{{ dashboardError }}</p>
                    <label *ngIf="canExportReports()" class="inline-flex h-11 items-center gap-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--card)] px-3 text-sm font-semibold text-[color:var(--foreground)] shadow-sm">
                        <lucide-icon name="file-down" class="h-4 w-4 text-[color:var(--text-subtle)]"></lucide-icon>
                        <select [(ngModel)]="reportProjectId" [disabled]="reportProjectsLoading || !!exportingFullReportFormat" aria-label="Project to include in the full report" class="max-w-[13rem] bg-transparent text-sm font-semibold focus:outline-none disabled:opacity-60">
                            <option value="">All projects</option>
                            <option *ngFor="let project of reportProjects" [value]="project.id">{{ project.name }}</option>
                        </select>
                    </label>
                    <div class="ml-auto inline-flex rounded-full border border-[color:var(--border)] bg-[color:var(--card)] p-1 shadow-sm">
                        <button
                            type="button"
                            (click)="setRange('7d')"
                            class="px-4 py-2 text-sm font-semibold rounded-full transition-colors"
                            [ngClass]="range === '7d' ? 'bg-[color:var(--primary)] text-[color:var(--primary-foreground)]' : 'text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)]'"
                        >
                            7D
                        </button>
                        <button
                            type="button"
                            (click)="setRange('30d')"
                            class="px-4 py-2 text-sm font-semibold rounded-full transition-colors"
                            [ngClass]="range === '30d' ? 'bg-[color:var(--primary)] text-[color:var(--primary-foreground)]' : 'text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)]'"
                        >
                            30D
                        </button>
                        <button
                            type="button"
                            (click)="setRange('ytd')"
                            class="px-4 py-2 text-sm font-semibold rounded-full transition-colors"
                            [ngClass]="range === 'ytd' ? 'bg-[color:var(--primary)] text-[color:var(--primary-foreground)]' : 'text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)]'"
                        >
                            YTD
                        </button>
                    </div>
                    <app-export-report-menu *ngIf="canExportReports()" [exportingFormat]="exportingFullReportFormat" (exportFormat)="exportFullReport($event)"></app-export-report-menu>
                </div>
            </div>

            <ng-container *ngIf="!loading; else dashboardSkeleton">
                <div *ngIf="!dashboardError && !stats.length" role="status" class="rounded-2xl border border-[color:var(--border)] bg-card p-8 text-center text-sm text-[color:var(--muted-foreground)]">
                    No visible properties or activity for this project.
                </div>
                <div *ngIf="statusBuckets.length" class="rounded-2xl border border-[color:var(--border)] bg-card p-5 shadow-sm">
                    <div class="mb-4">
                        <p class="text-lg font-semibold text-[color:var(--foreground)]">Property Status Distribution</p>
                    </div>
                    <div class="space-y-4">
                        <div class="flex items-center justify-between" *ngFor="let bucket of statusBuckets">
                            <div class="flex items-center gap-3">
                                <div class="h-4 w-4 rounded-full" [style.backgroundColor]="statusAccentByKey(bucket.key)"></div>
                                <span class="text-sm font-medium" [style.color]="statusAccentByKey(bucket.key)">{{ bucket.label }}</span>
                            </div>
                            <span
                                class="inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold"
                                [style.backgroundColor]="statusSurfaceByKey(bucket.key)"
                                [style.borderColor]="statusBorderByKey(bucket.key)"
                                [style.color]="statusAccentByKey(bucket.key)"
                            >
                                {{ bucket.value }}
                            </span>
                        </div>
                    </div>
                    <div class="mt-6 h-4 w-full overflow-hidden rounded-full bg-[color:var(--hover-strong)] flex">
                        <div *ngFor="let bucket of statusBuckets" class="h-full" [style.backgroundColor]="statusAccentByKey(bucket.key)" [style.width.%]="bucket.widthPct"></div>
                    </div>
                </div>

                <div *ngIf="stats.length" class="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    <button
                        type="button"
                        *ngFor="let stat of stats; let i = index"
                        (click)="selectStatus(stat)"
                        class="group relative h-full overflow-hidden rounded-2xl border border-[color:var(--border)] bg-card p-5 text-left shadow-sm transition hover:border-[color:var(--ring)] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)]/50"
                        [attr.aria-pressed]="selectedStatusKey === stat.statusKey"
                        [ngClass]="selectedStatusKey === stat.statusKey ? 'ring-2 ring-[color:var(--ring)]/40' : ''"
                    >
                        <div class="absolute inset-0 pointer-events-none">
                            <div class="absolute inset-0" [style.background]="statBackground(stat)"></div>
                            <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" class="absolute inset-0 h-full w-full opacity-[0.22]">
                                <defs>
                                    <linearGradient [attr.id]="'spark-bg-' + i" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" [attr.stop-color]="statAccent(stat)" stop-opacity="0.36"></stop>
                                        <stop offset="100%" [attr.stop-color]="statAccent(stat)" stop-opacity="0"></stop>
                                    </linearGradient>
                                </defs>
                                <path d="M -10 80 L 12 110 L 110 30 L 88 0 Z" [attr.fill]="'url(#spark-bg-' + i + ')'" opacity="0.55"></path>
                                <path [attr.d]="cardChartAreaPath(chartSeries(stat))" [attr.fill]="'url(#spark-bg-' + i + ')'"></path>
                                <polyline
                                    [attr.points]="cardChartLinePoints(chartSeries(stat))"
                                    [attr.stroke]="statAccent(stat)"
                                    stroke-width="8.5"
                                    fill="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-opacity="0.28"
                                ></polyline>
                            </svg>
                        </div>

                        <div class="relative">
                            <div class="flex items-center justify-between pb-2">
                                <p class="text-sm font-medium text-secondary">{{ stat.label }}</p>
                                <div class="rounded-full border p-2" [style.background]="statAccentBg(stat)" [style.borderColor]="statAccentBorder(stat)">
                                    <lucide-icon [name]="stat.icon" class="h-5 w-5" [style.color]="statAccent(stat)"></lucide-icon>
                                </div>
                            </div>
                            <div class="flex items-end justify-between gap-3">
                                <div class="text-4xl font-bold leading-none text-[color:var(--foreground)] tabular-nums">{{ stat.value }}</div>
                                <div *ngIf="stat.amount !== undefined && stat.amount !== null" class="text-xl font-bold leading-none text-[color:var(--foreground)] tabular-nums">
                                    {{ formatAmount(stat.amount) }}
                                </div>
                            </div>
                            <div class="mt-3 flex items-center justify-between text-xs">
                                <span
                                    class="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-semibold"
                                    [ngStyle]="cardBadgeStyle(stat)"
                                >
                                    {{ stat.change || 'No change' }}
                                </span>
                                <span class="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-subtle)]">{{ stat.metaLabel || trendLabel }}</span>
                            </div>
                        </div>
                    </button>
                </div>

                <div *ngIf="selectedStatusKey" #statusSection class="rounded-2xl border border-[color:var(--border)] bg-card p-5 shadow-sm scroll-mt-24">
                    <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <div class="min-w-0">
                            <p class="text-lg font-semibold text-[color:var(--foreground)]">
                                {{ selectedStatusLabel }} Properties
                            </p>
                            <p class="mt-1 text-sm text-[color:var(--muted-foreground)]">
                                Click a row to open View for full details.
                            </p>
                        </div>
                        <div class="flex items-center gap-2">
                            <a
                                routerLink="/developer/properties"
                                [queryParams]="dashboardQueryParams()"
                                class="inline-flex items-center gap-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--card)] px-3 py-2 text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--hover)]"
                            >
                                <lucide-icon name="home" class="h-4 w-4"></lucide-icon>
                                Manage Properties
                            </a>
                            <app-export-report-menu [disabled]="exportingStatus || statusLoading || !statusProperties.length" [exportingFormat]="exportingStatusFormat" (exportFormat)="exportStatusProperties($event)"></app-export-report-menu>
                            <button
                                type="button"
                                (click)="clearSelection()"
                                class="inline-flex items-center gap-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--card)] px-3 py-2 text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--hover)]"
                            >
                                <lucide-icon name="x" class="h-4 w-4"></lucide-icon>
                                Hide
                            </button>
                        </div>
                    </div>

                    <div *ngIf="statusLoading" class="rounded-xl border border-[color:var(--border)] bg-[color:var(--muted)] p-4 text-sm text-[color:var(--muted-foreground)]">
                        Loading properties…
                    </div>

                    <div *ngIf="!statusLoading && statusError" class="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-600">
                        {{ statusError }}
                    </div>

                    <div *ngIf="!statusLoading && !statusError && !statusProperties.length" class="rounded-xl border border-[color:var(--border)] bg-[color:var(--muted)] p-4 text-sm text-[color:var(--muted-foreground)]">
                        No properties found for this status.
                    </div>

                    <div *ngIf="!statusLoading && !statusError && statusProperties.length" class="overflow-x-auto">
                        <table class="min-w-full text-sm">
                            <thead>
                                <tr class="border-b border-[color:var(--border)] text-left text-[color:var(--text-subtle)]">
                                    <th class="py-3 pr-4 font-semibold">Unit</th>
                                    <th class="py-3 pr-4 font-semibold">Project</th>
                                    <th class="py-3 pr-4 font-semibold">Status</th>
                                    <th class="py-3 pr-4 font-semibold">Bedrooms</th>
                                    <th class="py-3 pr-4 font-semibold">Area</th>
                                    <th class="py-3 pr-4 font-semibold">Price</th>
                                    <th class="py-3 font-semibold">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    *ngFor="let p of statusProperties"
                                    class="border-b border-[color:var(--border)] last:border-0 hover:bg-[color:var(--hover)] cursor-pointer"
                                    (click)="goToProperty(p.id)"
                                    (keydown.enter)="goToProperty(p.id)"
                                    tabindex="0"
                                >
                                    <td class="py-3 pr-4">
                                        <p class="font-semibold text-[color:var(--foreground)]">{{ p.unitNumber || '—' }}</p>
                                        <p class="text-xs text-[color:var(--muted-foreground)] line-clamp-1">{{ p.title }}</p>
                                    </td>
                                    <td class="py-3 pr-4">
                                        <p class="font-medium text-[color:var(--foreground)]">{{ p.projectName || '—' }}</p>
                                        <p class="text-xs text-[color:var(--muted-foreground)]">{{ p.phaseName || '' }}</p>
                                    </td>
                                    <td class="py-3 pr-4">
                                        <span *ngIf="showPropertyStatusBadge(p)" class="inline-flex items-center rounded-full border border-[color:var(--border)] bg-[color:var(--card)] px-2.5 py-1 text-xs font-semibold text-[color:var(--foreground)]">
                                            {{ statusLabel(p) }}
                                        </span>
                                    </td>
                                    <td class="py-3 pr-4 font-medium text-[color:var(--foreground)]">{{ bedroomsLabel(p) }}</td>
                                    <td class="py-3 pr-4 font-medium text-[color:var(--foreground)]">{{ formatPropertyArea(p) }}</td>
                                    <td class="py-3 pr-4 font-semibold text-[color:var(--foreground)]">{{ formatPropertyPrice(p) }}</td>
                                    <td class="py-3">
                                        <a
                                            [routerLink]="['/developer/properties/view', p.id]"
                                            [queryParams]="dashboardQueryParams()"
                                            class="inline-flex items-center gap-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--card)] px-3 py-1.5 text-xs font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--hover)]"
                                            (click)="$event.stopPropagation()"
                                        >
                                            <lucide-icon name="external-link" class="h-3.5 w-3.5"></lucide-icon>
                                            View
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="space-y-6">
                    <div *ngIf="showPortfolioPerformanceSection()" class="rounded-2xl border border-[color:var(--border)] bg-card p-5 shadow-sm">
                        <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <div class="min-w-0">
                                <p class="text-lg font-semibold text-[color:var(--foreground)]">Portfolio Performance</p>
                                <p class="mt-1 text-sm text-[color:var(--muted-foreground)]">Properties assigned to your team and partner grants, with projected commission.</p>
                            </div>
                        </div>

                        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <div *ngFor="let metric of portfolioStats" class="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 shadow-sm">
                                <div class="flex items-center justify-between">
                                    <p class="text-sm font-medium text-secondary">{{ metric.label }}</p>
                                    <div class="rounded-full border p-2 bg-[color:var(--muted)] border-[color:var(--border)]">
                                        <lucide-icon [name]="metric.icon" class="h-5 w-5 text-[color:var(--foreground)]"></lucide-icon>
                                    </div>
                                </div>
                                <div class="mt-3 text-3xl font-bold leading-none text-[color:var(--foreground)] tabular-nums">{{ metric.primary }}</div>
                                <div *ngIf="metric.secondary" class="mt-2 text-sm font-semibold text-[color:var(--muted-foreground)] tabular-nums">{{ metric.secondary }}</div>
                                <div *ngIf="metric.helper" class="mt-2 text-xs text-[color:var(--text-subtle)] whitespace-pre-line">{{ metric.helper }}</div>
                            </div>
                        </div>
                    </div>

                    <div *ngIf="showTeamPerformanceSection()" class="rounded-2xl border border-[color:var(--border)] bg-card p-5 shadow-sm">
                        <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <div class="min-w-0">
                                <p class="text-lg font-semibold text-[color:var(--foreground)]">Team Performance</p>
                                <p class="mt-1 text-sm text-[color:var(--muted-foreground)]">{{ teamPerformanceDescription() }}</p>
                            </div>
                            <div class="flex items-center gap-3">
                                <app-export-report-menu *ngIf="canExportReports()" [disabled]="exportingTeamPerformance" [exportingFormat]="exportingTeamPerformanceFormat" (exportFormat)="exportTeamPerformance($event)"></app-export-report-menu>
                                <a
                                    *ngIf="canManageSalesTeam()"
                                    routerLink="/developer/sales-team"
                                    class="inline-flex items-center gap-2 rounded-lg bg-[color:var(--primary)] px-3 py-2 text-sm font-semibold text-[color:var(--primary-foreground)] shadow-sm transition hover:opacity-95"
                                >
                                    <lucide-icon name="external-link" class="h-4 w-4"></lucide-icon>
                                    Manage Team
                                </a>
                            </div>
                        </div>

                        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <div *ngFor="let metric of salesTeamStats" class="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 shadow-sm">
                                <div class="flex items-center justify-between">
                                    <p class="text-sm font-medium text-secondary">{{ metric.label }}</p>
                                    <div class="rounded-full border p-2 bg-[color:var(--muted)] border-[color:var(--border)]">
                                        <lucide-icon [name]="metric.icon" class="h-5 w-5 text-[color:var(--foreground)]"></lucide-icon>
                                    </div>
                                </div>
                                <div class="mt-3 text-3xl font-bold leading-none text-[color:var(--foreground)] tabular-nums">{{ metric.primary }}</div>
                                <div *ngIf="metric.secondary" class="mt-2 text-sm font-semibold text-[color:var(--muted-foreground)] tabular-nums">{{ metric.secondary }}</div>
                                <div *ngIf="metric.helper" class="mt-2 text-xs text-[color:var(--text-subtle)] whitespace-pre-line">{{ metric.helper }}</div>
                            </div>
                        </div>
                    </div>

                    <div *ngIf="showpartnerPerformanceSection()" class="rounded-2xl border border-[color:var(--border)] bg-card p-5 shadow-sm">
                        <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <div class="min-w-0">
                                <p class="text-lg font-semibold text-[color:var(--foreground)]">Partner Performance</p>
                                <p class="mt-1 text-sm text-[color:var(--muted-foreground)]">{{ partnerPerformanceDescription() }}</p>
                            </div>
                            <div class="flex items-center gap-3">
                                <app-export-report-menu *ngIf="canExportReports()" [disabled]="exportingPartnerStats" [exportingFormat]="exportingPartnerStatsFormat" (exportFormat)="exportPartnerStats($event)"></app-export-report-menu>
                                <a
                                    *ngIf="canManagepartners()"
                                    routerLink="/developer/partners"
                                    class="inline-flex items-center gap-2 rounded-lg bg-[color:var(--primary)] px-3 py-2 text-sm font-semibold text-[color:var(--primary-foreground)] shadow-sm transition hover:opacity-95"
                                >
                                    <lucide-icon name="external-link" class="h-4 w-4"></lucide-icon>
                                    Manage Partners
                                </a>
                            </div>
                        </div>

                        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <div *ngFor="let metric of partnerStats" class="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 shadow-sm">
                                <div class="flex items-center justify-between">
                                    <p class="text-sm font-medium text-secondary">{{ metric.label }}</p>
                                    <div class="rounded-full border p-2 bg-[color:var(--muted)] border-[color:var(--border)]">
                                        <lucide-icon [name]="metric.icon" class="h-5 w-5 text-[color:var(--foreground)]"></lucide-icon>
                                    </div>
                                </div>
                                <div class="mt-3 text-3xl font-bold leading-none text-[color:var(--foreground)] tabular-nums">{{ metric.primary }}</div>
                                <div *ngIf="metric.secondary" class="mt-2 text-sm font-semibold text-[color:var(--muted-foreground)] tabular-nums">{{ metric.secondary }}</div>
                                <div *ngIf="metric.helper" class="mt-2 text-xs text-[color:var(--text-subtle)] whitespace-pre-line">{{ metric.helper }}</div>
                            </div>
                        </div>
                    </div>

                    <div *ngIf="postSaleFollowUpEnabled && canReadPostSale() && postSaleSummary" class="rounded-2xl border border-[color:var(--border)] bg-card p-5 shadow-sm">
                        <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <div class="min-w-0">
                                <p class="text-lg font-semibold text-[color:var(--foreground)]">Post-Sale Follow-Up</p>
                                <p class="mt-1 text-sm text-[color:var(--muted-foreground)]">Operational servicing for contracted investors, documents, and due payments.</p>
                            </div>
                            <a
                                routerLink="/developer/reservations"
                                class="inline-flex items-center gap-2 rounded-lg bg-[color:var(--primary)] px-3 py-2 text-sm font-semibold text-[color:var(--primary-foreground)] shadow-sm transition hover:opacity-95"
                            >
                                <lucide-icon name="external-link" class="h-4 w-4"></lucide-icon>
                                Open Follow-Up
                            </a>
                        </div>

                        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <div class="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 shadow-sm">
                                <p class="text-sm font-medium text-secondary">Active cases</p>
                                <p class="mt-3 text-3xl font-bold leading-none text-[color:var(--foreground)] tabular-nums">{{ postSaleSummary.totalCases }}</p>
                                <p class="mt-2 text-xs text-[color:var(--text-subtle)]">Documents complete: {{ postSaleSummary.documentsComplete }}</p>
                            </div>
                            <div class="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 shadow-sm">
                                <p class="text-sm font-medium text-secondary">Payment due</p>
                                <p class="mt-3 text-3xl font-bold leading-none text-[color:var(--foreground)] tabular-nums">{{ postSaleSummary.paymentDue | number:'1.0-0' }}</p>
                                <p class="mt-2 text-xs text-[color:var(--text-subtle)]">Schedule active: {{ postSaleSummary.paymentScheduleActive }}</p>
                            </div>
                            <div class="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 shadow-sm">
                                <p class="text-sm font-medium text-secondary">Overdue</p>
                                <p class="mt-3 text-3xl font-bold leading-none text-[color:var(--foreground)] tabular-nums">{{ postSaleSummary.overdue | number:'1.0-0' }}</p>
                                <p class="mt-2 text-xs text-[color:var(--text-subtle)]">Completed: {{ postSaleSummary.paymentCompleted | number:'1.0-0' }}</p>
                            </div>
                            <div class="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] p-4 shadow-sm">
                                <p class="text-sm font-medium text-secondary">Next due date</p>
                                <p class="mt-3 text-lg font-bold leading-tight text-[color:var(--foreground)]">{{ formatDateLabel(postSaleSummary.nextDueDate) }}</p>
                                <p class="mt-2 text-xs text-[color:var(--text-subtle)]">Documents pending: {{ postSaleSummary.documentsPending }}</p>
                            </div>
                        </div>
                    </div>
                </div>

            </ng-container>

                <div class="grid gap-6 lg:grid-cols-3" *ngIf="!loading; else cardsSkeleton">
                    <div class="rounded-2xl border border-[color:var(--border)] bg-card p-5 shadow-sm">
                        <div class="mb-4 flex items-center justify-between">
                            <p class="text-lg font-semibold text-[color:var(--foreground)]">Projects</p>
                            <a routerLink="/developer/projects" class="rounded-lg px-3 py-1 text-sm text-[color:var(--primary)] hover:bg-[color:var(--hover)]">View all</a>
                        </div>
                        <div class="space-y-4">
                            <a
                                *ngFor="let project of recentProjects.slice(0, projectsPreviewLimit)"
                                [routerLink]="['/developer/projects', project.id]"
                                class="group flex items-center justify-between gap-3 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-3 transition hover:bg-[color:var(--hover)]"
                            >
                                <div class="flex items-center gap-3 min-w-0">
                                    <div class="h-12 w-16 overflow-hidden rounded-lg border border-[color:var(--border)] bg-[color:var(--muted)] shrink-0">
                                        <img
                                            *ngIf="project.imageUrl && !projectPreviewImageFailed[project.id]; else projectImageFallback"
                                            [src]="project.imageUrl"
                                            [alt]="project.name"
                                            class="h-full w-full object-cover"
                                            loading="lazy"
                                            decoding="async"
                                            (error)="onProjectPreviewImageError(project.id)"
                                        />
                                        <ng-template #projectImageFallback>
                                            <div class="flex h-full w-full items-center justify-center text-[color:var(--text-subtle)]">
                                                <lucide-icon name="image" class="h-5 w-5 opacity-60"></lucide-icon>
                                            </div>
                                        </ng-template>
                                    </div>
                                    <div class="min-w-0">
                                        <p class="font-medium text-[color:var(--foreground)] line-clamp-1">{{ project.name }}</p>
                                        <p class="text-sm text-secondary">{{ project.units }} units</p>
                                    </div>
                                </div>
                                <span class="shrink-0 rounded-full px-3 py-1 text-xs font-semibold border" [ngClass]="project.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-700 border-slate-200'">
                                    {{ project.status }}
                                </span>
                            </a>
                        </div>
                        <div *ngIf="recentProjects.length > projectsPreviewLimit" class="mt-6 flex justify-center">
                            <button
                                type="button"
                            (click)="showMoreProjects()"
                            class="inline-flex items-center justify-center gap-2 rounded-full border border-[color:var(--border)] bg-[color:var(--card)] px-6 py-3 text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--hover)]"
                        >
                            <lucide-icon name="plus" class="h-4 w-4"></lucide-icon>
                            Show more
                        </button>
                    </div>
                </div>

                <div class="rounded-2xl border border-[color:var(--border)] bg-card p-5 shadow-sm">
                    <div class="mb-4 flex items-center justify-between">
                        <p class="text-lg font-semibold text-[color:var(--foreground)]">Properties</p>
                        <a routerLink="/developer/properties" [queryParams]="dashboardQueryParams()" class="rounded-lg px-3 py-1 text-sm text-[color:var(--primary)] hover:bg-[color:var(--hover)]">View all</a>
                    </div>
                    <div class="space-y-4">
                        <a
                            *ngFor="let p of recentProperties.slice(0, propertiesPreviewLimit)"
                            [routerLink]="['/developer/properties/view', p.id]"
                            [queryParams]="dashboardQueryParams()"
                            class="group block rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-3 transition hover:bg-[color:var(--hover)]"
                        >
                            <div class="flex items-start gap-3">
                                <div class="h-12 w-16 overflow-hidden rounded-lg border border-[color:var(--border)] bg-[color:var(--muted)] shrink-0">
                                    <img
                                        *ngIf="propertyPreviewImage(p) && !propertyPreviewImageFailed[p.id]; else propertyImageFallback"
                                        [src]="propertyPreviewImage(p) || ''"
                                        [alt]="p.title || 'Property'"
                                        class="h-full w-full object-cover"
                                        loading="lazy"
                                        decoding="async"
                                        (error)="onPropertyPreviewImageError(p.id)"
                                    />
                                    <ng-template #propertyImageFallback>
                                        <div class="flex h-full w-full items-center justify-center text-[color:var(--text-subtle)]">
                                            <lucide-icon name="image" class="h-5 w-5 opacity-60"></lucide-icon>
                                        </div>
                                    </ng-template>
                                </div>
                                <div class="min-w-0 flex-1">
                                    <div class="flex items-start justify-between gap-3">
                                        <div class="min-w-0">
                                            <p class="font-semibold text-[color:var(--foreground)] line-clamp-1">{{ p.title || 'Untitled property' }}</p>
                                            <p class="text-xs text-[color:var(--muted-foreground)] line-clamp-1">
                                                <span *ngIf="p.unitNumber">Unit {{ p.unitNumber }}</span>
                                                <span *ngIf="p.unitNumber && p.projectName"> · </span>
                                                <span *ngIf="p.projectName">{{ p.projectName }}</span>
                                            </p>
                                        </div>
                                        <span *ngIf="showPropertyStatusBadge(p)" class="shrink-0 rounded-full border border-[color:var(--border)] bg-[color:var(--muted)] px-2.5 py-1 text-[11px] font-semibold text-[color:var(--foreground)]">
                                            {{ statusLabel(p) }}
                                        </span>
                                    </div>
                                    <p class="mt-2 text-sm font-semibold text-[color:var(--foreground)]">{{ formatPropertyPrice(p) }}</p>
                                </div>
                            </div>
                        </a>
                    </div>
                    <div *ngIf="recentProperties.length > propertiesPreviewLimit" class="mt-6 flex justify-center">
                        <button
                            type="button"
                            (click)="showMoreProperties()"
                            class="inline-flex items-center justify-center gap-2 rounded-full border border-[color:var(--border)] bg-[color:var(--card)] px-6 py-3 text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--hover)]"
                        >
                            <lucide-icon name="plus" class="h-4 w-4"></lucide-icon>
                            Show more
                        </button>
                    </div>
                </div>

                <!-- "Lead visibility" (reservations:read) governs this card: a member
                     with the toggle off must not see the lead feed at all (the API
                     returns an empty feed for them; hide the card entirely). -->
                <div *ngIf="canViewRecentLeads()" class="rounded-2xl border border-[color:var(--border)] bg-card p-5 shadow-sm">
                    <div class="mb-4 flex items-center justify-between">
                        <p class="text-lg font-semibold text-[color:var(--foreground)]">Recent Leads</p>
                        <a routerLink="/developer/leads" [queryParams]="dashboardQueryParams()" class="rounded-lg px-3 py-1 text-sm text-[color:var(--primary)] hover:bg-[color:var(--hover)]">View all</a>
                    </div>
                    <div class="space-y-4">
                        <div *ngFor="let lead of recentLeads" class="group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-[color:var(--border)] pb-3 last:border-0 last:pb-0">
                            <div class="min-w-0">
                                <p class="font-medium text-[color:var(--foreground)]">{{ lead.name }}</p>
                                <p class="text-sm text-secondary">{{ lead.property }}</p>
                                <p class="text-xs text-[color:var(--text-subtle)] mt-1">{{ formatDate(lead.date) }}</p>
                            </div>
                            <div class="flex shrink-0 items-center gap-2">
                                <span class="inline-flex min-h-7 min-w-[4.75rem] items-center justify-center whitespace-nowrap rounded-full border px-3 py-1 text-center text-xs font-semibold leading-none" [ngStyle]="leadStatusStyle(lead.status)">
                                    {{ leadStatusLabel(lead.status) }}
                                </span>
                                <div class="flex gap-1">
                                    <button type="button" class="h-8 w-8 rounded-full border border-[color:var(--border)] text-[color:var(--text-subtle)] hover:text-[color:var(--primary)] hover:bg-[color:var(--hover)]">
                                        <lucide-icon name="eye" class="h-4 w-4 mx-auto"></lucide-icon>
                                    </button>
                                    <button type="button" class="h-8 w-8 rounded-full border border-[color:var(--border)] text-[color:var(--text-subtle)] hover:text-[color:var(--primary)] hover:bg-[color:var(--hover)]">
                                        <lucide-icon name="mail" class="h-4 w-4 mx-auto"></lucide-icon>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <ng-template #dashboardSkeleton>
            <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <div *ngFor="let i of skeletonArray" class="h-28 rounded-2xl border border-[color:var(--border)] bg-card shadow-sm animate-pulse"></div>
            </div>
        </ng-template>

        <ng-template #cardsSkeleton>
            <div class="grid gap-6 lg:grid-cols-3">
                <div *ngFor="let i of skeletonArray.slice(0,3)" class="rounded-2xl border border-[color:var(--border)] bg-card p-5 space-y-4 shadow-sm animate-pulse">
                    <div class="h-4 w-32 bg-[color:var(--hover-strong)] rounded"></div>
                    <div class="space-y-3">
                        <div *ngFor="let j of skeletonArray.slice(0,3)" class="h-12 bg-[color:var(--hover-strong)] rounded"></div>
                    </div>
                </div>
            </div>
        </ng-template>
    
